import { useMemo, useReducer, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import type { FirmwareUpgrade } from "@/api/schemas/sites";

type FirmwareView = "table" | "calendar";
type FirmwarePhaseFilter = "all" | "upcoming" | "completed";
type ExportRow = Record<string, string | number | boolean | null | undefined>;

type FirmwareFilterState = {
  view: FirmwareView;
  phaseFilter: FirmwarePhaseFilter;
  networkFilter: string;
  productFilter: string;
  statusFilter: string;
  versionFilter: string;
  scheduledFilter: string;
  completedFilter: string;
};

function buildFirmwareColumns({
  networkFilter,
  productFilter,
  statusFilter,
  versionFilter,
  scheduledFilter,
  completedFilter,
  setFilters,
  fmtDateTime,
}: {
  networkFilter: string;
  productFilter: string;
  statusFilter: string;
  versionFilter: string;
  scheduledFilter: string;
  completedFilter: string;
  setFilters: (p: Partial<FirmwareFilterState>) => void;
  fmtDateTime: (iso: string | null | undefined) => string;
}): DataTableColumn<FirmwareUpgrade>[] {
  return [
    {
      key: "network",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Network</span>
          <Input value={networkFilter} onChange={(e) => setFilters({ networkFilter: e.target.value })} placeholder="Contains" className="h-8" />
        </div>
      ),
      width: "1.2fr",
      sortable: true,
      sortValue: (r) => r.networkName,
      render: (r) => <span className="text-sm text-fg">{r.networkName}</span>,
    },
    {
      key: "products",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Product types</span>
          <Input value={productFilter} onChange={(e) => setFilters({ productFilter: e.target.value })} placeholder="Contains" className="h-8" />
        </div>
      ),
      width: "1fr",
      render: (r) => <span className="font-mono text-xs text-fg-dim">{r.productTypes.join(", ") || "-"}</span>,
    },
    {
      key: "status",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Status</span>
          <Input value={statusFilter} onChange={(e) => setFilters({ statusFilter: e.target.value })} placeholder="Contains" className="h-8" />
        </div>
      ),
      width: "0.9fr",
      render: (r) => (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-fg-dim">{r.status}</span>
          <Badge variant={r.phase === "completed" ? "ok" : "info"} size="sm">{r.phase}</Badge>
        </div>
      ),
    },
    {
      key: "version",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Versions</span>
          <Input value={versionFilter} onChange={(e) => setFilters({ versionFilter: e.target.value })} placeholder="From/To" className="h-8" />
        </div>
      ),
      width: "1fr",
      render: (r) => <span className="font-mono text-xs text-fg-dim">{r.fromVersion ?? "-"} → {r.toVersion ?? "-"}</span>,
    },
    {
      key: "scheduled",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Scheduled</span>
          <Input value={scheduledFilter} onChange={(e) => setFilters({ scheduledFilter: e.target.value })} placeholder="Contains" className="h-8" />
        </div>
      ),
      width: "1fr",
      sortable: true,
      sortValue: (r) => r.scheduledAt ?? "",
      render: (r) => <span className="text-xs text-fg-dim">{fmtDateTime(r.scheduledAt)}</span>,
    },
    {
      key: "completed",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Completed</span>
          <Input value={completedFilter} onChange={(e) => setFilters({ completedFilter: e.target.value })} placeholder="Contains" className="h-8" />
        </div>
      ),
      width: "1fr",
      sortable: true,
      sortValue: (r) => r.completedAt ?? "",
      render: (r) => <span className="text-xs text-fg-dim">{fmtDateTime(r.completedAt)}</span>,
    },
  ];
}

function FirmwareCalendarView({
  filtered,
  rangeScoped,
  byDay,
  fmtDateTime,
}: {
  filtered: FirmwareUpgrade[];
  rangeScoped: FirmwareUpgrade[];
  byDay: [string, { label: string; rows: FirmwareUpgrade[] }][];
  fmtDateTime: (iso: string | null | undefined) => string;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3">
      <div className="font-mono text-[11px] text-fg-mute">
        {filtered.length} / {rangeScoped.length} upgrades
      </div>
      {byDay.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line p-4 text-sm text-fg-mute">
          No firmware upgrades in selected timeframe.
        </div>
      ) : (
        byDay.map(([day, bucket]) => (
          <div key={day} className="rounded-lg border border-line/70 bg-surface-tint/20 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-fg">
              <CalendarDays size={14} className="text-violet" />
              {bucket.label}
            </div>
            <div className="flex flex-col gap-2">
              {bucket.rows.map((row) => (
                <div
                  key={`${row.upgradeId}-${row.productTypes.join("-")}`}
                  className="flex flex-col gap-1 rounded-md border border-line/60 bg-surface px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-fg">{row.networkName}</span>
                    <Badge variant={row.phase === "completed" ? "ok" : "info"} size="sm">
                      {row.phase}
                    </Badge>
                  </div>
                  <div className="font-mono text-[11px] text-fg-mute">
                    {row.productTypes.join(", ") || "-"} · {row.fromVersion ?? "-"} → {row.toVersion ?? "-"}
                  </div>
                  <div className="text-xs text-fg-dim">
                    Scheduled: {fmtDateTime(row.scheduledAt)}
                    {row.completedAt ? ` · Completed: ${fmtDateTime(row.completedAt)}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function FirmwareUpgradesSection({
  upgrades,
  range,
  refreshing,
  onRefresh,
  rangeOptions,
  isWithinRange,
  fmtDateTime,
  dayKeyForTimezone,
  dayLabelForTimezone,
  exportRowsCsv,
  exportRowsXlsx,
}: {
  upgrades: FirmwareUpgrade[];
  range: string;
  refreshing: boolean;
  onRefresh: () => void;
  rangeOptions: Array<{ id: string; label: string }>;
  isWithinRange: (iso: string | null | undefined, rangeId: string) => boolean;
  fmtDateTime: (iso: string | null | undefined) => string;
  dayKeyForTimezone: (iso: string, timezone: string) => string;
  dayLabelForTimezone: (iso: string, timezone: string) => string;
  exportRowsCsv: (rows: ExportRow[], fileName: string) => void;
  exportRowsXlsx: (rows: ExportRow[], fileName: string) => Promise<void>;
}) {
  const [
    { view, phaseFilter, networkFilter, productFilter, statusFilter, versionFilter, scheduledFilter, completedFilter },
    setFilters,
  ] = useReducer(
    (s: FirmwareFilterState, p: Partial<FirmwareFilterState>) => ({ ...s, ...p }),
    { view: "table", phaseFilter: "all", networkFilter: "", productFilter: "", statusFilter: "", versionFilter: "", scheduledFilter: "", completedFilter: "" },
  );
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const rangeScoped = useMemo(
    () =>
      upgrades.filter((u) =>
        isWithinRange(u.scheduledAt ?? u.completedAt, range),
      ),
    [upgrades, range, isWithinRange],
  );

  const filtered = useMemo(() => {
    const networkQ = networkFilter.trim().toLowerCase();
    const productQ = productFilter.trim().toLowerCase();
    const statusQ = statusFilter.trim().toLowerCase();
    const versionQ = versionFilter.trim().toLowerCase();
    const scheduledQ = scheduledFilter.trim().toLowerCase();
    const completedQ = completedFilter.trim().toLowerCase();

    return rangeScoped.filter((u) => {
      if (phaseFilter !== "all" && u.phase !== phaseFilter) return false;
      if (networkQ && !u.networkName.toLowerCase().includes(networkQ))
        return false;
      if (
        productQ &&
        !u.productTypes.join(", ").toLowerCase().includes(productQ)
      ) {
        return false;
      }
      if (statusQ && !u.status.toLowerCase().includes(statusQ)) return false;
      if (
        versionQ &&
        !(
          (u.fromVersion ?? "").toLowerCase().includes(versionQ) ||
          (u.toVersion ?? "").toLowerCase().includes(versionQ)
        )
      ) {
        return false;
      }
      if (
        scheduledQ &&
        !fmtDateTime(u.scheduledAt).toLowerCase().includes(scheduledQ)
      ) {
        return false;
      }
      if (
        completedQ &&
        !fmtDateTime(u.completedAt).toLowerCase().includes(completedQ)
      ) {
        return false;
      }
      return true;
    });
  }, [
    completedFilter,
    networkFilter,
    phaseFilter,
    productFilter,
    rangeScoped,
    scheduledFilter,
    statusFilter,
    versionFilter,
    fmtDateTime,
  ]);

  const byDay = useMemo(() => {
    const map = new Map<string, { label: string; rows: FirmwareUpgrade[] }>();
    for (const row of filtered) {
      const anchor = row.scheduledAt ?? row.completedAt;
      if (!anchor) continue;
      const tz = row.timezone || "UTC";
      const key = dayKeyForTimezone(anchor, tz);
      const bucket = map.get(key) ?? {
        label: `${dayLabelForTimezone(anchor, tz)} (${tz})`,
        rows: [],
      };
      bucket.rows.push(row);
      map.set(key, bucket);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, dayKeyForTimezone, dayLabelForTimezone]);

  const cols = buildFirmwareColumns({
    networkFilter,
    productFilter,
    statusFilter,
    versionFilter,
    scheduledFilter,
    completedFilter,
    setFilters,
    fmtDateTime,
  });

  const firmwareExportRows = useMemo(
    () =>
      filtered.map((row) => ({
        Network: row.networkName,
        "Product Types": row.productTypes.join(", "),
        Status: row.status,
        Phase: row.phase,
        "From Version": row.fromVersion ?? "",
        "To Version": row.toVersion ?? "",
        Scheduled: row.scheduledAt ?? "",
        Completed: row.completedAt ?? "",
      })),
    [filtered],
  );

  function downloadFirmwareCsv() {
    exportRowsCsv(firmwareExportRows, "firmware-upgrades");
  }

  async function downloadFirmwareXlsx() {
    setExportingXlsx(true);
    try {
      await exportRowsXlsx(firmwareExportRows, "firmware-upgrades");
    } finally {
      setExportingXlsx(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <CardTitle>Firmware upgrades</CardTitle>
            <CardSub>
              Upcoming and completed upgrades within{" "}
              {rangeOptions.find((r) => r.id === range)?.label}
            </CardSub>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 px-1 py-1">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-mute">
                View
              </span>
              <Button
                size="sm"
                variant={view === "table" ? "primary" : "ghost"}
                onClick={() => setFilters({ view: "table" })}
              >
                Table
              </Button>
              <Button
                size="sm"
                variant={view === "calendar" ? "primary" : "ghost"}
                onClick={() => setFilters({ view: "calendar" })}
              >
                Calendar
              </Button>
            </div>
            <span className="hidden text-fg-mute/60 sm:inline">|</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={firmwareExportRows.length === 0}
                >
                  <Download size={12} className="text-fg-dim" />
                  Download
                  <ChevronDown size={11} className="text-fg-mute" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={downloadFirmwareCsv}>
                  <FileText size={12} className="mr-1 text-fg-dim" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => void downloadFirmwareXlsx()}
                  disabled={exportingXlsx}
                >
                  <FileSpreadsheet size={12} className="mr-1 text-fg-dim" />
                  {exportingXlsx ? "Generating XLSX..." : "XLSX"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw
                size={12}
                className={cn("text-fg-dim", refreshing && "animate-spin")}
              />
              Refresh
            </Button>
          </div>
        </div>

        {view === "table" ? (
          <DataTable
            columns={cols}
            data={filtered}
            rowKey={(r) => r.upgradeId || `${r.networkId}-${r.scheduledAt}`}
            showTotal={false}
            toolbar={
              <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <span className="font-mono text-[11px] text-fg-mute">
                  {filtered.length} / {rangeScoped.length} upgrades
                </span>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-mute">
                    Filters
                  </span>
                  <Button
                    size="sm"
                    variant={phaseFilter === "all" ? "primary" : "ghost"}
                    onClick={() => setFilters({ phaseFilter: "all" })}
                  >
                    All
                  </Button>
                  <Button
                    size="sm"
                    variant={phaseFilter === "upcoming" ? "primary" : "ghost"}
                    onClick={() => setFilters({ phaseFilter: "upcoming" })}
                  >
                    Upcoming
                  </Button>
                  <Button
                    size="sm"
                    variant={phaseFilter === "completed" ? "primary" : "ghost"}
                    onClick={() => setFilters({ phaseFilter: "completed" })}
                  >
                    Completed
                  </Button>
                </div>
              </div>
            }
          />
        ) : (
          <FirmwareCalendarView
            filtered={filtered}
            rangeScoped={rangeScoped}
            byDay={byDay}
            fmtDateTime={fmtDateTime}
          />
        )}
      </CardContent>
    </Card>
  );
}
